import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Fade, 
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TimerIcon from '@mui/icons-material/Timer';
import BaseRound from './BaseRound';
import { useGame } from '../../contexts/GameContext';
import { pantomimeCategories } from '../../data/gameData';

export default function PantomimeRound() {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentItems, setCurrentItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showingItems, setShowingItems] = useState(false);
  const [showInitialItem, setShowInitialItem] = useState(false);
  const [initialCountdown, setInitialCountdown] = useState(3);
  const [timeLeft, setTimeLeft] = useState(60);
  const [timerActive, setTimerActive] = useState(false);
  const [timerEnded, setTimerEnded] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultSuccess, setResultSuccess] = useState(null);
  const { state } = useGame();

  useEffect(() => {
    let timer;
    if (showInitialItem && initialCountdown > 0) {
      timer = setInterval(() => {
        setInitialCountdown(prev => prev - 1);
      }, 1000);
    } else if (showInitialItem && initialCountdown === 0) {
      setShowInitialItem(false);
      setTimerActive(true);
    }
    return () => clearInterval(timer);
  }, [showInitialItem, initialCountdown]);

  useEffect(() => {
    let timer;
    if (timerActive && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
    } else if (timeLeft === 0 && timerActive) {
      setTimerActive(false);
      setTimerEnded(true);
    }
    return () => clearInterval(timer);
  }, [timerActive, timeLeft]);

  const selectCategory = (category) => {
    setSelectedCategory(category);
    const shuffled = [...category.items].sort(() => 0.5 - Math.random());
    setCurrentItems(shuffled.slice(0, 3));
    setShowingItems(true);
  };

  const selectItem = (item) => {
    setSelectedItem(item);
    setShowingItems(false);
    setShowInitialItem(true);
    setInitialCountdown(3);
  };

  const handleResult = (success) => {
    setTimerActive(false);
    setShowResult(true);
    setResultSuccess(success);
  };

  const resultData = showResult ? {
    title: selectedItem,
    success: resultSuccess,
    drinks: 2,
    player: state.activePlayer,
    points: resultSuccess ? 2 : 0
  } : null;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <BaseRound
      instructions={showingItems 
        ? `${state.activePlayer}, wähle einen Begriff zum Darstellen!`
        : selectedItem && !showResult
          ? "Die anderen Spieler müssen jetzt raten!"
          : `${state.activePlayer}, wähle eine Kategorie zum Darstellen!`}
      showResult={showResult}
      resultData={resultData}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!selectedCategory && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {pantomimeCategories.map((category, index) => (
              <Button
                key={index}
                variant="outlined"
                color="primary"
                onClick={() => selectCategory(category)}
                sx={{ height: '80px' }}
              >
                {category.name}
              </Button>
            ))}
          </Box>
        )}

        {showingItems && (
          <Fade in timeout={500}>
            <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom>
                Wähle einen Begriff:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {currentItems.map((item, index) => (
                  <Button
                    key={index}
                    variant="contained"
                    color="secondary"
                    onClick={() => selectItem(item)}
                  >
                    {item}
                  </Button>
                ))}
              </Box>
            </Paper>
          </Fade>
        )}

        {selectedItem && !showingItems && !showResult && (
          <Fade in timeout={500}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper 
                sx={{ 
                  p: 3, 
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'primary.main',
                }}
              >
                {showInitialItem && (
                  <>
                    <Typography variant="body1" align="center" color="primary">
                      Darzustellender Begriff:
                    </Typography>
                    <Typography variant="h5" align="center" sx={{ mt: 1 }}>
                      {selectedItem}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      align="center" 
                      sx={{ mt: 2, color: 'text.secondary' }}
                    >
                      Der Begriff wird in {initialCountdown} Sekunden ausgeblendet...
                    </Typography>
                  </>
                )}

                {(timerActive || timerEnded) && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    <CircularProgress
                      variant="determinate"
                      value={(timeLeft / 60) * 100}
                      size={80}
                      thickness={4}
                      sx={{
                        color: timeLeft > 10 ? 'primary.main' : 'error.main'
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1
                    }}>
                      <TimerIcon sx={{ 
                        color: timeLeft > 10 ? 'primary.main' : 'error.main',
                        animation: timeLeft <= 10 ? 'pulse 1s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.2)' },
                          '100%': { transform: 'scale(1)' }
                        }
                      }} />
                      <Typography 
                        variant="body1" 
                        component="div" 
                        color={timeLeft > 10 ? 'primary' : 'error'}
                        sx={{ fontSize: '0.9rem' }}
                      >
                        {formatTime(timeLeft)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {timerEnded && (
                  <Typography 
                    variant="h5" 
                    align="center" 
                    sx={{ mt: 3 }}
                  >
                    {selectedItem}
                  </Typography>
                )}
              </Paper>

              {(timerActive || timerEnded) && !showResult && (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  mt: 2
                }}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={() => handleResult(true)}
                    startIcon={<CheckCircleIcon />}
                    sx={{ py: 2 }}
                  >
                    Erraten!
                  </Button>
                  {timerEnded && (
                    <Button
                      variant="contained"
                      color="error"
                      fullWidth
                      onClick={() => handleResult(false)}
                      startIcon={<CancelIcon />}
                      sx={{ py: 2 }}
                    >
                      Nicht erraten
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </Box>
    </BaseRound>
  );
} 